import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { IconArrowLeft, IconPencil, IconTrash } from "@tabler/icons-react";
import { Heading, Text } from "../../../../components/common/typography";
import { Button } from "../../../../components/common/button";
import { Chip } from "../../../../components/common/chip";
import { Dialog } from "../../../../components/common/dialog";
import { SectionCard } from "../../../../components/common/section-card/section-card";
import { ShimmerLoader, Shimmer } from "../../../../components/common/shimmer/shimmer";
import { useLoyaltyPromotions, useLoyaltyPromotionMutations } from "../../hooks/useLoyaltyPromotions";
import { promotionStatus, formatPromoDate, promotionScopeLabel } from "../../utils/promotion-status";
import { showErrorToast, showSuccessToast } from "../../../../utils/toasts";
import { AppRoutes } from "../../../../common/routes/app_routes";

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1">
      <Text variant="bodyXs" tone="muted">{label}</Text>
      <div>{children}</div>
    </div>
  );
}

export function AdminLoyaltyPromotionDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: promotions, isLoading } = useLoyaltyPromotions();
  const { remove } = useLoyaltyPromotionMutations();
  const [confirming, setConfirming] = useState(false);

  const promotion = promotions?.find((p) => p.id === id);
  const back = () => navigate(AppRoutes.ADMIN_LOYALTY_PROMOTIONS);

  const confirmDelete = async () => {
    if (!promotion) return;
    try {
      await remove(promotion.id);
      showSuccessToast("Promotion deleted.");
      back();
    } catch {
      showErrorToast("Failed to delete promotion.");
    } finally {
      setConfirming(false);
    }
  };

  const status = promotion ? promotionStatus(promotion) : null;

  return (
    <div className="flex min-h-full w-full flex-col bg-bgLight">
      <div className="flex flex-1 flex-col gap-4 px-8 pt-6 pb-6">
        <div className="flex items-center gap-2">
          <Button
            variant="ghostAccent"
            size="sm"
            leftIcon={<IconArrowLeft size={18} />}
            onClick={back}
            className="!h-auto !px-0 !py-0"
          >
            <Heading variant="h3" tone="inherit">Promotions</Heading>
          </Button>
          <Text variant="bodySm" tone="muted">/</Text>
          <Heading variant="h3" tone="dark">{promotion?.name ?? "Promotion"}</Heading>
        </div>

        {isLoading ? (
          <SectionCard shadow title="Promotion" className="max-w-3xl">
            <ShimmerLoader message="Loading promotion…">
              <Shimmer className="h-48 rounded-xl" />
            </ShimmerLoader>
          </SectionCard>
        ) : !promotion ? (
          <SectionCard shadow title="Not found" className="max-w-3xl">
            <Text variant="body" tone="muted">This promotion no longer exists.</Text>
          </SectionCard>
        ) : (
          <SectionCard
            shadow
            title={promotion.name}
            className="max-w-3xl gap-5"
            action={
              <div className="flex items-center gap-2">
                <Button
                  variant="secondary"
                  size="sm"
                  leftIcon={<IconPencil size={16} />}
                  onClick={() => navigate(AppRoutes.ADMIN_LOYALTY_PROMOTION_EDIT.replace(":id", promotion.id))}
                >
                  Edit
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  leftIcon={<IconTrash size={16} />}
                  onClick={() => setConfirming(true)}
                >
                  Delete
                </Button>
              </div>
            }
          >
            <div className="grid grid-cols-2 gap-5 sm:grid-cols-3">
              <Field label="Status">
                {status && <Chip variant={status.variant}>{status.label}</Chip>}
              </Field>
              <Field label="Multiplier">
                <Text variant="bodyBold" tone="dark">{promotion.multiplier}× points</Text>
              </Field>
              <Field label="Applies to">
                <Text variant="body" tone="dark">{promotionScopeLabel(promotion)}</Text>
              </Field>
              <Field label="Starts">
                <Text variant="body" tone="dark">{formatPromoDate(promotion.startsAt)}</Text>
              </Field>
              <Field label="Ends">
                <Text variant="body" tone="dark">{formatPromoDate(promotion.endsAt)}</Text>
              </Field>
              <Field label="Enabled">
                <Text variant="body" tone="dark">{promotion.enabled ? "Yes" : "No"}</Text>
              </Field>
            </div>
          </SectionCard>
        )}
      </div>

      <Dialog
        open={confirming}
        setOpen={(o) => !o && setConfirming(false)}
        title="Delete promotion?"
        text={promotion ? `"${promotion.name}" will be removed. This can't be undone.` : ""}
        buttonText="Delete"
        cancelText="Cancel"
        onConfirm={confirmDelete}
      />
    </div>
  );
}
